/**
 * Codec NDEF da etiqueta digital: os bytes que o app grava na tag NFC e a
 * leitura do `labelId` de volta.
 *
 * Origem: #900/#902 — 2026-10-09 15:28 +0200 (módulo Etiquetas inteligentes).
 *
 * Por quê: o chip guarda só o id; o Firestore é a fonte da verdade (#805).
 * Esta é a FONTE do formato: o gerador de contrato executa este encoder e
 * grava `packages/types/contract/cases/nfc-ndef.json`. Os codecs do Android
 * (`core/nfc/LabelNdefCodec.kt`) e do iOS (#905) são espelhos manuais e são
 * conferidos byte a byte contra esse vetor. Mexeu aqui → `npm run contract`.
 *
 * Dados — mensagem NDEF (decisão D2 da #900), 118 B para um id de 20 chars:
 *
 *   | # | Registro                         | Conteúdo                                | Bytes |
 *   |---|----------------------------------|-----------------------------------------|-------|
 *   | 1 | URI, TNF 1, `U`, prefixo 0x04    | `app.etiquetachef.com/labels/<labelId>` | 53    |
 *   | 2 | Text, TNF 1, `T`, UTF-8, `en`    | `<labelId>`                             | 27    |
 *   | 3 | AAR, TNF 4, `android.com:pkg`    | `com.etiquetachef.app`                  | 38    |
 *
 * Todos os registros são curtos (SR), sem id (IL=0) e sem chunk. A URI vai
 * primeiro porque é o registro que o despacho do Android casa com o
 * intent-filter `NDEF_DISCOVERED` e o que a leitura em segundo plano do iPhone
 * usa. O Text guarda o id cru, para a leitura sobreviver a uma troca de
 * domínio. O AAR vai por último, como manda a convenção do Android.
 *
 * Na tag, a mensagem vai dentro do TLV `03 <len> … FE`; quem monta o TLV é o
 * SO (`Ndef.writeNdefMessage` / `writeNDEF`), não este codec.
 *
 * Tag aceita na gravação: capacidade ≥ a da NTAG213 (`isSupportedTagCapacity`);
 * a D1 era "só NTAG213" até 2026-10-09 17:32 +0200.
 *
 * Contrato da leitura: `readLabelNdef(bytes)` devolve uma união discriminada
 * (`LabelNdefRead`), na ordem URI → Text. O fallback pelo UID do chip (tag
 * apagada ou adulterada) é de quem chama — este codec não conhece o servidor.
 *
 * Usado por: `scripts/gen-contract-snapshot.ts` (vetor); testes deste pacote;
 * espelhos no Android e no iOS.
 *
 * Dicas: o id aceito é o mesmo charset do `BindNfcTagInputSchema`
 * (`[A-Za-z0-9_-]{1,128}`): ids do Firestore não têm barra, e uma barra no id
 * viraria caminho de subcoleção no servidor.
 *
 * Issues: #900 (D2), #902 (esta fase), #805 (bancada).
 */

/** Host e caminho da URL gravada (a mesma rota `/labels/:id` do `apps/app`). */
export const NFC_LABEL_URI_HOST = 'app.etiquetachef.com';
export const NFC_LABEL_URI_PATH = '/labels/';
/** Pacote do Android Application Record. */
export const NFC_AAR_PACKAGE = 'com.etiquetachef.app';
/** Idioma do registro Text — fixo, o conteúdo é um id e não texto humano. */
export const NFC_TEXT_LANG = 'en';

/**
 * Resposta do `GET_VERSION` (comando `0x60`) de uma NTAG213 genuína (datasheet
 * NXP NTAG213/215/216, tabela "GET_VERSION response"). A 215 termina em `0x11`
 * e a 216 em `0x13`; só este valor é aceito (decisão D1 da #900).
 */
export const NTAG213_GET_VERSION: readonly number[] = [0x00, 0x04, 0x04, 0x02, 0x01, 0x00, 0x0f, 0x03];

/**
 * Maior mensagem que a NTAG213 guarda: 144 B de usuário − o TLV (`03 <len>` +
 * `FE`) = 141; o Android reporta `Ndef.maxSize` = 137 nos controladores NXP, e
 * é esse o teto usado aqui (auditoria 2026-10-09, #900 B4: o encoder aceitava
 * id de 128 chars, 334 B, que só falhava no `writeNdefMessage`).
 */
export const NTAG213_MAX_NDEF_BYTES = 137;

/** Charset do id (igual ao `BindNfcTagInputSchema`). */
const LABEL_ID = /^[A-Za-z0-9_-]{1,128}$/;

const TNF_WELL_KNOWN = 0x01;
const TNF_EXTERNAL = 0x04;
const FLAG_MB = 0x80;
const FLAG_ME = 0x40;
const FLAG_CF = 0x20;
const FLAG_SR = 0x10;
const FLAG_IL = 0x08;
const URI_PREFIX_HTTPS = 0x04;
/** Códigos de prefixo URI (NFC Forum RTD-URI) que a leitura reconhece. */
const URI_PREFIXES: Readonly<Record<number, string>> = {
  0x00: '',
  0x01: 'http://www.',
  0x02: 'https://www.',
  0x03: 'http://',
  0x04: 'https://',
};

/** Um registro NDEF decodificado (só o que a leitura usa). */
export type NdefRecord = { tnf: number; type: Uint8Array; payload: Uint8Array };

/** De onde a leitura tirou o id. */
export const LabelNdefSource = { URI: 'URI', TEXT: 'TEXT' } as const;
export type LabelNdefSource = (typeof LabelNdefSource)[keyof typeof LabelNdefSource];

/** Resultado da leitura de uma mensagem NDEF. */
export const LabelNdefReadKind = {
  /** Achou um `labelId` nosso. */
  LABEL: 'LABEL',
  /** Mensagem vazia (tag virgem ou apagada). */
  EMPTY: 'EMPTY',
  /** Bytes truncados ou registro que este codec não aceita (chunk). */
  MALFORMED: 'MALFORMED',
  /** NDEF válido, mas sem id nosso (só AAR, URL de outro domínio, id inválido). */
  NO_LABEL: 'NO_LABEL',
} as const;
export type LabelNdefReadKind = (typeof LabelNdefReadKind)[keyof typeof LabelNdefReadKind];

export type LabelNdefRead =
  | { kind: typeof LabelNdefReadKind.LABEL; labelId: string; source: LabelNdefSource }
  | { kind: typeof LabelNdefReadKind.EMPTY }
  | { kind: typeof LabelNdefReadKind.MALFORMED }
  | { kind: typeof LabelNdefReadKind.NO_LABEL };

const utf8 = new TextEncoder();

/** Um registro curto (SR), sem id. `first`/`last` acendem MB/ME. */
function shortRecord(tnf: number, type: Uint8Array, payload: Uint8Array, first: boolean, last: boolean): number[] {
  if (type.length > 0xff || payload.length > 0xff) throw new RangeError('registro curto excede 255 B');
  const header = (first ? FLAG_MB : 0) | (last ? FLAG_ME : 0) | FLAG_SR | tnf;
  return [header, type.length, payload.length, ...type, ...payload];
}

/** O id serve para gravar? (Charset do callable; sem barra.) */
export function isNfcLabelId(labelId: string): boolean {
  return LABEL_ID.test(labelId);
}

/**
 * Monta a mensagem NDEF da etiqueta (URI + Text + AAR). Lança `RangeError`
 * para id fora do charset — gravar uma tag que o próprio app não relê seria
 * pior que recusar — e acima de [NTAG213_MAX_NDEF_BYTES] (teto da NTAG213).
 *
 * Origem: #900/#902 — 2026-10-09 15:28 +0200.
 *
 * Usado por: `scripts/gen-contract-snapshot.ts` (vetor `nfc-ndef.json`, que o
 * `LabelNdefCodec` do Android e o iOS conferem byte a byte); testes deste pacote.
 */
export function encodeLabelNdef(labelId: string): Uint8Array {
  if (!isNfcLabelId(labelId)) throw new RangeError(`labelId inválido para NFC: ${labelId}`);
  const uri = shortRecord(
    TNF_WELL_KNOWN,
    utf8.encode('U'),
    Uint8Array.from([URI_PREFIX_HTTPS, ...utf8.encode(`${NFC_LABEL_URI_HOST}${NFC_LABEL_URI_PATH}${labelId}`)]),
    true,
    false,
  );
  const lang = utf8.encode(NFC_TEXT_LANG);
  const text = shortRecord(
    TNF_WELL_KNOWN,
    utf8.encode('T'),
    // Byte de status: bit 7 = 0 (UTF-8), bits 0–5 = tamanho do código de idioma.
    Uint8Array.from([lang.length, ...lang, ...utf8.encode(labelId)]),
    false,
    false,
  );
  const aar = shortRecord(TNF_EXTERNAL, utf8.encode('android.com:pkg'), utf8.encode(NFC_AAR_PACKAGE), false, true);
  const message = Uint8Array.from([...uri, ...text, ...aar]);
  if (message.length > NTAG213_MAX_NDEF_BYTES) {
    throw new RangeError(`mensagem de ${String(message.length)} B não cabe na NTAG213 (${String(NTAG213_MAX_NDEF_BYTES)} B)`);
  }
  return message;
}

/**
 * Decodifica os registros de uma mensagem NDEF (sem o TLV). `null` quando os
 * bytes não fecham: tamanho declarado além do fim, registro em chunk, ou o
 * fim chegando antes do ME.
 */
export function parseNdefRecords(bytes: Uint8Array): NdefRecord[] | null {
  const records: NdefRecord[] = [];
  let i = 0;
  while (i < bytes.length) {
    const header = bytes[i] ?? 0;
    if ((header & FLAG_CF) !== 0) return null;
    const sr = (header & FLAG_SR) !== 0;
    const il = (header & FLAG_IL) !== 0;
    let p = i + 1;
    const typeLen = bytes[p++];
    let payloadLen: number | undefined;
    if (sr) {
      payloadLen = bytes[p++];
    } else {
      // Registro longo: tamanho em 4 B big-endian.
      const b = bytes.subarray(p, p + 4);
      p += 4;
      payloadLen = b.length === 4 ? b.reduce((acc, byte) => acc * 256 + byte, 0) : undefined;
    }
    const idLen = il ? bytes[p++] : 0;
    if (typeLen === undefined || payloadLen === undefined || idLen === undefined) return null;
    const typeStart = p;
    const payloadStart = typeStart + typeLen + idLen;
    const end = payloadStart + payloadLen;
    if (end > bytes.length) return null;
    records.push({
      tnf: header & 0x07,
      type: bytes.slice(typeStart, typeStart + typeLen),
      payload: bytes.slice(payloadStart, end),
    });
    i = end;
    // Bytes depois do ME são ignorados: a mensagem acabou ali.
    if ((header & FLAG_ME) !== 0) return records;
  }
  // Acabou sem ME: mensagem truncada.
  return records.length === 0 ? [] : null;
}

const decoder = new TextDecoder('utf-8', { fatal: true });

function decodeUtf8(bytes: Uint8Array): string | null {
  try {
    return decoder.decode(bytes);
  } catch {
    return null;
  }
}

function isWellKnown(r: NdefRecord, type: string): boolean {
  return r.tnf === TNF_WELL_KNOWN && r.type.length === 1 && r.type[0] === type.charCodeAt(0);
}

/** `labelId` de um registro URI nosso, ou `null` (outro domínio, outro caminho, id inválido). */
function labelIdFromUri(r: NdefRecord): string | null {
  const prefix = URI_PREFIXES[r.payload[0] ?? -1];
  const rest = decodeUtf8(r.payload.subarray(1));
  if (prefix === undefined || rest === null) return null;
  // Host exato: `www.app.etiquetachef.com` não existe e não é nosso.
  const match = /^https?:\/\/([^/?#]+)(\/[^?#]*)/i.exec(prefix + rest);
  if (match === null || match[1]?.toLowerCase() !== NFC_LABEL_URI_HOST) return null;
  const path = match[2] ?? '';
  if (!path.startsWith(NFC_LABEL_URI_PATH)) return null;
  const id = path.slice(NFC_LABEL_URI_PATH.length).replace(/\/$/, '');
  return isNfcLabelId(id) ? id : null;
}

/**
 * `labelId` de um registro Text (UTF-8), ou `null`. Aceita o que o QR aceita
 * (`LabelQrPayload`): o id cru ou a forma JSON `{"l":"<id>"}` — auditoria
 * 2026-10-09 (#900, M2): o Android já aceitava o JSON e o TS não, e o iOS
 * (#905) espelharia um dos dois. O vetor `cases/nfc-ndef.json` cobre os dois.
 */
function labelIdFromText(r: NdefRecord): string | null {
  const status = r.payload[0];
  if (status === undefined || (status & 0x80) !== 0) return null; // UTF-16: não é nosso
  const text = (decodeUtf8(r.payload.subarray(1 + (status & 0x3f))) ?? '').trim();
  const id = text.startsWith('{') ? jsonLabelId(text) : text;
  return id !== null && isNfcLabelId(id) ? id : null;
}

/** O `l` de um `{"l":"<id>", ...}`; `null` se não for esse formato. */
function jsonLabelId(text: string): string | null {
  try {
    const parsed: unknown = JSON.parse(text);
    const l = parsed !== null && typeof parsed === 'object' ? (parsed as { l?: unknown }).l : undefined;
    return typeof l === 'string' ? l.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Lê o `labelId` de uma mensagem NDEF, na ordem URI → Text (ver o cabeçalho).
 *
 * Origem: #900/#902 — 2026-10-09 15:28 +0200.
 *
 * Usado por: `scripts/gen-contract-snapshot.ts` (casos `read` do vetor, espelho
 * do `LabelNdefCodec.read` do Android); testes deste pacote.
 */
export function readLabelNdef(bytes: Uint8Array): LabelNdefRead {
  if (bytes.length === 0) return { kind: LabelNdefReadKind.EMPTY };
  const records = parseNdefRecords(bytes);
  if (records === null) return { kind: LabelNdefReadKind.MALFORMED };
  if (records.length === 0 || records.every((r) => r.tnf === 0x00)) return { kind: LabelNdefReadKind.EMPTY };
  for (const r of records) {
    if (!isWellKnown(r, 'U')) continue;
    const id = labelIdFromUri(r);
    if (id !== null) return { kind: LabelNdefReadKind.LABEL, labelId: id, source: LabelNdefSource.URI };
  }
  for (const r of records) {
    if (!isWellKnown(r, 'T')) continue;
    const id = labelIdFromText(r);
    if (id !== null) return { kind: LabelNdefReadKind.LABEL, labelId: id, source: LabelNdefSource.TEXT };
  }
  return { kind: LabelNdefReadKind.NO_LABEL };
}

/**
 * A tag aceita a etiqueta? Regra do owner, 2026-10-09 17:32 +0200 (#900,
 * revisão da D1): "tem que ser compatível com todas as tags acima da NTAG213,
 * porém não pode deixar gravar mais do que a NTAG213 suporta".
 *  - Compatível = qualquer tag NDEF com capacidade ≥ a da NTAG213
 *    ([NTAG213_MAX_NDEF_BYTES]) e UID de 7 B (o `tagUid` do vínculo,
 *    `LabelNfcSchema`): NTAG215/216 e clones (que nem sempre respondem o
 *    `GET_VERSION` como a NXP — a bancada da #805 tinha um). Chip com UID de
 *    outro tamanho (MIFARE Classic, 4 B) é recusado antes, pelo UID.
 *  - O conteúdo nunca passa da NTAG213: o `encodeLabelNdef` recusa acima de
 *    [NTAG213_MAX_NDEF_BYTES]. Gravada numa 215, a etiqueta cabe numa 213.
 * Tag menor (Ultralight, 48 B) é recusada pela capacidade.
 */
export function isSupportedTagCapacity(maxNdefBytes: number): boolean {
  return Number.isInteger(maxNdefBytes) && maxNdefBytes >= NTAG213_MAX_NDEF_BYTES;
}

/** Bytes em hex maiúsculo (vetor do contrato e logs). */
export function bytesToHex(bytes: ArrayLike<number>): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0').toUpperCase()).join('');
}
